import React, { useState } from 'react';
import { PhoneIcon, ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline';

const EscalationSettings = ({ settings, onInputChange, myBusiness, API_BASE_URL }) => {
  const [testingSMS, setTestingSMS] = useState(false);
  const [testMessage, setTestMessage] = useState("");

  const handleTogglePhone = (enabled) => {
    onInputChange("is_escalate_phone", enabled);
    if (enabled) {
      // Keep current SMS setting
    } else {
      // If disabling phone escalation, ensure SMS is enabled
      onInputChange("escalate_sms_notifications", true);
    }
  };

  const testEscalationSMS = async () => {
    setTestingSMS(true);
    setTestMessage("");

    try {
      const response = await fetch(
        `${API_BASE_URL}/webhooks/test-escalation-sms`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            business_phone: myBusiness,
            test_message: "Test escalation SMS from settings panel",
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setTestMessage("✅ Test SMS sent successfully!");
      } else {
        setTestMessage(
          "❌ SMS test failed: " + (data.message || "Unknown error")
        );
      }
    } catch (error) {
      console.error("Error testing SMS:", error);
      setTestMessage("❌ SMS test failed: " + error.message);
    } finally {
      setTestingSMS(false);
      setTimeout(() => setTestMessage(""), 5000);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          📞 Call Escalation Settings
        </h3>
        <p className="text-gray-600 mb-6">
          Configure how your AI assistant handles requests to speak with
          management.
        </p>

        <div className="space-y-6">
          {/* Manager Call Toggle */}
          <div
            className={`p-6 rounded-lg border-2 ${
              settings.is_escalate_phone
                ? "border-blue-200 bg-blue-50"
                : "border-gray-200 bg-white"
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div
                  className={`h-12 w-12 rounded-lg flex items-center justify-center ${
                    settings.is_escalate_phone ? "bg-blue-100" : "bg-gray-100"
                  }`}
                >
                  <PhoneIcon
                    className={`h-6 w-6 ${
                      settings.is_escalate_phone
                        ? "text-blue-600"
                        : "text-gray-600"
                    }`}
                  />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">
                    Manager Call Mode
                  </h4>
                  <p className="text-sm text-gray-500">
                    System calls management when escalation is requested
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.is_escalate_phone}
                  onChange={(e) => handleTogglePhone(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {settings.is_escalate_phone ? (
              <div className="space-y-4">
                <div className="text-sm text-blue-700 space-y-1">
                  <p>
                    ✅ AI will immediately call management with customer
                    details
                  </p>
                  <p>
                    ✅ Customer informed: "Someone will contact you shortly"
                  </p>
                  <p>✅ SMS and email notifications sent simultaneously</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Manager Phone Number
                  </label>
                  <input
                    type="tel"
                    value={settings.escalate_phone_number}
                    onChange={(e) =>
                      onInputChange("escalate_phone_number", e.target.value)
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+1 (555) 123-4567"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Management will receive a call with customer details and
                    callback instructions.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-600 space-y-1">
                <p>
                  📱 Notification mode: Management notified via SMS and email
                  only
                </p>
                <p>
                  💬 AI says: "I'll have someone call you back within 2 hours"
                </p>
                <p>📞 AI collects customer info for callback</p>
              </div>
            )}
          </div>

          {/* SMS Notifications */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h4 className="font-medium text-gray-900 mb-4">
              Notification Settings
            </h4>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h5 className="text-sm font-medium text-gray-900">
                    SMS Notifications
                  </h5>
                  <p className="text-sm text-gray-500">
                    Send text alerts when customers request escalation (always
                    recommended)
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Recipients:{" "}
                    {settings.escalate_phone_number ||
                      settings.owner_phone ||
                      "No phone number set"}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.escalate_sms_notifications}
                    onChange={(e) =>
                      onInputChange("escalate_sms_notifications", e.target.checked)
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h5 className="text-sm font-medium text-gray-900">
                    Email Notifications
                  </h5>
                  <p className="text-sm text-gray-500">
                    Send email alerts when customers request escalation
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Recipients:{" "}
                    {settings.owner_email || "No email address set"}
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.escalate_email_notifications}
                    onChange={(e) =>
                      onInputChange("escalate_email_notifications", e.target.checked)
                    }
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>

            {/* Test SMS Button */}
            {settings.escalate_sms_notifications &&
              (settings.escalate_phone_number || settings.owner_phone) && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="text-sm font-medium text-gray-700">
                        Test SMS Functionality
                      </h5>
                      <p className="text-xs text-gray-500">
                        Send a test escalation SMS to verify setup
                      </p>
                    </div>
                    <button
                      onClick={testEscalationSMS}
                      disabled={testingSMS}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm flex items-center space-x-2"
                    >
                      {testingSMS ? (
                        <>
                          <ClockIcon className="h-4 w-4 animate-spin" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <PhoneIcon className="h-4 w-4" />
                          <span>Test SMS</span>
                        </>
                      )}
                    </button>
                  </div>

                  {testMessage && (
                    <div
                      className={`mt-2 p-2 rounded text-sm ${
                        testMessage.includes("✅")
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {testMessage}
                    </div>
                  )}
                </div>
              )}
          </div>

          {/* How It Works */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
            <h4 className="font-medium text-blue-900 mb-4">
              How Escalation Works
            </h4>

            {settings.is_escalate_phone ? (
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-blue-600">1</span>
                  </div>
                  <p className="text-sm text-blue-800">
                    Customer asks: "Can I speak to your manager?"
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-blue-600">2</span>
                  </div>
                  <p className="text-sm text-blue-800">
                    AI collects customer details and reason for escalation
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-blue-600">3</span>
                  </div>
                  <p className="text-sm text-blue-800">
                    AI responds: "Someone will contact you shortly. Thank you!"
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-blue-600">4</span>
                  </div>
                  <p className="text-sm text-blue-800">
                    System immediately calls{" "}
                    {settings.escalate_phone_number || "management"} with
                    customer summary
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-blue-600">5</span>
                  </div>
                  <p className="text-sm text-blue-800">
                    SMS and email notifications sent with customer contact details
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-blue-600">1</span>
                  </div>
                  <p className="text-sm text-blue-800">
                    Customer asks: "Can I speak to your manager?"
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-blue-600">2</span>
                  </div>
                  <p className="text-sm text-blue-800">
                    AI responds: "I'll have someone call you back within 2 hours"
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-blue-600">3</span>
                  </div>
                  <p className="text-sm text-blue-800">
                    AI collects: customer name, phone number, and reason for escalation
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-blue-600">4</span>
                  </div>
                  <p className="text-sm text-blue-800">
                    SMS and email notifications sent with all customer details for callback
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-blue-600">5</span>
                  </div>
                  <p className="text-sm text-blue-800">
                    AI says: "Thank you — someone will follow up shortly."
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Warning for missing contact info */}
          {!settings.escalate_phone_number && !settings.owner_phone && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-yellow-800">
                    Setup Required
                  </h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    {settings.is_escalate_phone
                      ? "Please set a manager phone number above to enable manager calls."
                      : "Please set an owner phone number in Business Info to receive SMS notifications."}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(EscalationSettings);
